import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import * as React from 'react'
import { connect } from 'react-redux'
import type { DropdownStringOption } from 'types/ui'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { updateUserSettings } from '@disco/state-management/actions/UserActions'
import { PERSONAL_LIBRARY, TEAM_LIBRARY } from '@disco/constants/GroupTypes'
import {
  getCustomFieldKeys,
  getMultiLevelSetting,
} from '@disco/state-management/selectors/user'
import Row from 'components/common/row'
import HelpTooltip from 'components/common/tooltip'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import Checkbox from 'components/ui/checkbox'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Label from 'components/ui/label'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import MetadataHoverSettings from 'pages/settings/display/metadata-hover-settings'
import type { UserReducer } from '@disco/state-management/reducers/user'
const COLUMN_WIDTHS = [
  {
    value: 'Default',
    label: 'Default',
  },
  {
    value: 'Large',
    label: 'Large',
  },
  {
    value: 'Larger',
    label: 'Larger',
  },
]
const COMPONENT_HEIGHTS = [
  {
    value: 'Default',
    label: 'Default',
  },
  {
    value: 'Compact',
    label: 'Compact',
  },
]
const DEFAULT_VIEWS = [
  {
    value: TEAM_LIBRARY,
    label: 'Browse',
  },
  {
    value: PERSONAL_LIBRARY,
    label: 'Starred',
  },
]

function mapStateToProps(state) {
  return {
    user: state.user,
    customFields: getCustomFieldKeys(state),
  }
}

const actions = {
  updateUserSettings,
  sendMixpanelEvent,
}
type Props = {
  user: UserReducer
  updateUserSettings: typeof updateUserSettings
  sendMixpanelEvent: typeof sendMixpanelEvent
}

class DisplaySettings extends React.Component<
  Props,
  {
    explorerColumnWidth: string
    // example: "Large"
    explorerComponentHeight: string
    explorerDefaultView: string
    explorer_trackhover_fields: string[]
    explorer_trackpane_fields: string[]
    hideAutoPlaylists: boolean
  }
> {
  state = {
    hideAutoPlaylists: get(
      this.props.user,
      'settings.hide_auto_playlists',
      false,
    ),
    explorerColumnWidth: get(
      this.props.user,
      'settings.explorer_column_width',
      'Default',
    ),
    explorerComponentHeight: get(
      this.props.user,
      'settings.explorer_component_height',
      'Default',
    ),
    explorerDefaultView: get(
      this.props.user,
      'settings.explorer_default_view',
      TEAM_LIBRARY,
    ),
    explorer_trackhover_fields: getMultiLevelSetting.explorer_trackhover_fields(
      this.props.user,
    ).settingUser,
    explorer_trackpane_fields: getMultiLevelSetting.explorer_trackpane_fields(
      this.props.user,
    ).settingUser,
  }

  componentDidMount() {
    this.props.sendMixpanelEvent('Open app settings tab')
  }

  _handleAutoPlaylistsChange = () => {
    this.setState({
      hideAutoPlaylists: !this.state.hideAutoPlaylists,
    })
  }
  _handleExplorerColumnWidthChange = (option: DropdownStringOption) => {
    this.setState({
      explorerColumnWidth: option.value,
    })
  }
  _handleExplorerComponentHeight = (option: DropdownStringOption) => {
    this.setState({
      explorerComponentHeight: option.value,
    })
  }
  _handleExplorerDefaultViewChange = (option: DropdownStringOption) => {
    this.setState({
      explorerDefaultView: option.value,
    })
  }
  _handleUserSettingsSubmit = () => {
    let {
      hideAutoPlaylists,
      explorerColumnWidth,
      explorerComponentHeight,
      explorerDefaultView,
      explorer_trackhover_fields,
      explorer_trackpane_fields,
    } = this.state

    if (
      isEqual(
        explorer_trackhover_fields,
        getMultiLevelSetting.explorer_trackhover_fields(this.props.user)
          .settingBusiness,
      )
    ) {
      explorer_trackhover_fields = undefined
    }

    if (
      isEqual(
        explorer_trackpane_fields,
        getMultiLevelSetting.explorer_trackpane_fields(this.props.user)
          .settingBusiness,
      )
    ) {
      explorer_trackpane_fields = undefined
    }

    this.props.updateUserSettings(
      {
        settings: {
          hide_auto_playlists: hideAutoPlaylists,
          explorer_column_width: explorerColumnWidth,
          explorer_component_height: explorerComponentHeight,
          explorer_default_view: explorerDefaultView,
          explorer_hover_fields: undefined,
          explorer_trackview_fields: undefined,
          explorer_trackhover_fields: explorer_trackhover_fields,
          explorer_trackpane_fields: explorer_trackpane_fields,
        },
      },
      true,
    )
  }

  render() {
    const { hideAutoPlaylists } = this.state
    const loadingUser = Boolean(this.props.user.updating)
    return (
      <>
        <DocumentHeadTags title="Display Settings | Explorer | DISCO" />
        <PageHeader title="Display Settings" />
        <PageContainer>
          <div className="Admin_Section">
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Checkbox
                  label="Hide auto-created album playlists"
                  checked={Boolean(hideAutoPlaylists)}
                  onChange={this._handleAutoPlaylistsChange}
                >
                  <HelpTooltip id="tooltip_hide_auto_created_album_playlists">
                    <p>
                      You&#39;ll see some playlists in DISCO that have no
                      creator listed, and which you didn&#39;t create. These are
                      auto-playlists, playlists created automatically because
                      two or more tracks share artist and album metadata.
                    </p>
                    <p>
                      Check this box if you’d like to hide these auto-playlists
                      from your Explorer. (This setting will just affect you,
                      and will take effect next time you click on a Catalog or
                      channel.)
                    </p>
                  </HelpTooltip>
                </Checkbox>
              </div>
            </Row>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Label
                  caption="Explorer column width"
                  tooltipId="tooltip_settings_column_size"
                />
                <CustomDropdown
                  value={this.state.explorerColumnWidth}
                  multi={false}
                  options={COLUMN_WIDTHS}
                  onChange={this._handleExplorerColumnWidthChange}
                />
              </div>
            </Row>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Label
                  caption="Explorer display density"
                  tooltipContent="This setting controls the height of the items in the Explorer (where your saved playlists live). Use the Default setting if you want things a bit more spacious, or Compact if you’d like to see more on the page."
                />
                <CustomDropdown
                  value={this.state.explorerComponentHeight}
                  multi={false}
                  options={COMPONENT_HEIGHTS}
                  onChange={this._handleExplorerComponentHeight}
                />
              </div>
            </Row>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Label
                  caption="Default view"
                  tooltipId="tooltip_settings_default_view"
                />
                <CustomDropdown
                  value={this.state.explorerDefaultView}
                  multi={false}
                  options={DEFAULT_VIEWS}
                  onChange={this._handleExplorerDefaultViewChange}
                />
              </div>
            </Row>
            <MetadataHoverSettings
              value={{
                hover: this.state.explorer_trackhover_fields,
                pane: this.state.explorer_trackpane_fields,
              }}
              onChange={value => {
                this.setState({
                  explorer_trackhover_fields: value.hover,
                  explorer_trackpane_fields: value.pane,
                })
              }}
            />
            <br />
            <Button
              appearance="primary"
              caption="Update Display Settings"
              onClick={this._handleUserSettingsSubmit}
              progressCaption="Updating Settings"
              progressIndicator={loadingUser}
            />
          </div>
        </PageContainer>
      </>
    )
  }
}

export default connect(mapStateToProps, actions)(DisplaySettings) as any
